<script lang="ts">
	import CheckCircleIcon from "@lucide/svelte/icons/check-circle";
	import XCircleIcon from "@lucide/svelte/icons/x-circle";
	import Section from "#lib/web-ui/primitives/section.svelte";
	import { getTemplateContext } from "../template-context.svelte";

	const templateContext = getTemplateContext();

	const ourBenefits = [
		"No fees for selling your home",
		"Highest price for off-market sale",
		"We buy houses as-is. Zero cleanup, showings or repairs",
		"No out-of-pocket expenses or fees",
		"7-day closings, or move on your schedule"
	];

	const traditionalDrawbacks = [
		"Up to 6% of home sale price and additional fees",
		"Unpredictable haggling and low offers",
		"Hassles of repairs and cleaning up the property",
		"Agent recommendations for repairs just to list",
		"2 to 3 month long sales process"
	];
</script>

<Section class="bg-foreground">
	<!-- Heading Section -->
	<div class="mx-auto max-w-2xl text-center">
		<h2 class="text-primary text-lg font-bold">Compare Your Options</h2>
		<p
			class="mt-2 text-4xl font-semibold tracking-tight text-pretty text-white sm:text-5xl sm:text-balance"
		>
			Benefits of Selling to {templateContext.siteName}
		</p>
		<p class="mx-auto mt-6 max-w-xl text-lg/8 text-pretty text-gray-300">
			See how we make selling your home easier, faster, and more profitable than traditional
			methods.
		</p>
	</div>

	<!-- Comparison Cards -->
	<div class="mx-auto mt-16 grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-2">
		<!-- Left column: Your Advantages -->
		<div
			class="relative overflow-hidden rounded-2xl bg-white/5 p-8 ring-1 ring-white/10 backdrop-blur-sm transition-all hover:bg-white/10 lg:p-10"
		>
			<!-- Decorative corner accent -->
			<div
				class="bg-primary absolute top-0 right-0 h-24 w-24 translate-x-8 -translate-y-8 rounded-full opacity-20 blur-2xl"
			></div>

			<div class="relative">
				<div class="flex items-center gap-3">
					<div class="bg-primary/20 rounded-lg p-2">
						<CheckCircleIcon class="text-primary h-6 w-6" />
					</div>
					<h3 class="text-2xl font-semibold text-white">
						Why {templateContext.siteName}
					</h3>
				</div>

				<ul class="mt-8 space-y-5">
					{#each ourBenefits as benefit}
						<li class="flex gap-4">
							<div class="flex-shrink-0 pt-1">
								<CheckCircleIcon class="text-primary h-5 w-5" />
							</div>
							<p class="text-base/7 text-gray-300">
								{benefit}
							</p>
						</li>
					{/each}
				</ul>
			</div>
		</div>

		<!-- Right column: Traditional Process -->
		<div
			class="relative overflow-hidden rounded-2xl bg-white/5 p-8 ring-1 ring-white/10 backdrop-blur-sm transition-all hover:bg-white/10 lg:p-10"
		>
			<!-- Decorative corner accent -->
			<div
				class="absolute top-0 right-0 h-24 w-24 translate-x-8 -translate-y-8 rounded-full bg-red-500 opacity-20 blur-2xl"
			></div>

			<div class="relative">
				<div class="flex items-center gap-3">
					<div class="rounded-lg bg-red-500/20 p-2">
						<XCircleIcon class="h-6 w-6 text-red-400" />
					</div>
					<h3 class="text-2xl font-semibold text-white">Traditional Sales Process</h3>
				</div>

				<ul class="mt-8 space-y-5">
					{#each traditionalDrawbacks as drawback}
						<li class="flex gap-4">
							<div class="flex-shrink-0 pt-1">
								<XCircleIcon class="h-5 w-5 text-red-400" />
							</div>
							<p class="text-base/7 text-gray-300">
								{drawback}
							</p>
						</li>
					{/each}
				</ul>
			</div>
		</div>
	</div>
</Section>
